import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea.jsx';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, CheckCircle, XCircle, Clock, MapPin, User, Calendar, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DeletionRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/deletion-requests`);
      setRequests(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Löschanfragen');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    setProcessingId(requestId);
    try {
      await axios.put(`${API}/superadmin/deletion-requests/${requestId}?action=approve`);
      toast.success('Standort wurde gelöscht');
      fetchRequests();
    } catch (error) {
      toast.error('Fehler beim Genehmigen');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId) => {
    if (!rejectionReason.trim()) {
      toast.error('Bitte geben Sie einen Ablehnungsgrund an');
      return;
    }
    setProcessingId(requestId);
    try {
      await axios.put(`${API}/superadmin/deletion-requests/${requestId}?action=reject&rejection_reason=${encodeURIComponent(rejectionReason)}`);
      toast.success('Löschanfrage wurde abgelehnt');
      setRejectingId(null);
      setRejectionReason('');
      fetchRequests();
    } catch (error) {
      toast.error('Fehler beim Ablehnen');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" /> Ausstehend</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" /> Genehmigt</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" /> Abgelehnt</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const processedRequests = requests.filter(r => r.status !== 'pending');

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
            Standort-Löschanfragen
          </h1>
          <p className="text-muted-foreground mt-2">
            Genehmigen oder lehnen Sie Löschanfragen von Kunden ab
          </p>
        </div>

        {pendingRequests.length === 0 && processedRequests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Trash2 className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">Keine Löschanfragen vorhanden</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Pending Requests */}
            {pendingRequests.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-yellow-700">
                    <Clock className="h-5 w-5" />
                    Ausstehende Anfragen ({pendingRequests.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {pendingRequests.map((request) => (
                    <div key={request.id} className="border rounded-lg p-4 bg-yellow-50/50">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-5 w-5 text-yellow-600" />
                            <span className="font-semibold text-lg">{request.location_name}</span>
                            {getStatusBadge(request.status)}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Building2 className="h-4 w-4" />
                              {request.customer_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="h-4 w-4" />
                              {request.requested_by_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {formatDate(request.created_at)}
                            </span>
                          </div>
                          {request.reason && (
                            <p className="text-sm bg-white dark:bg-gray-900 p-2 rounded border">
                              <strong>Grund:</strong> {request.reason}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          {rejectingId === request.id ? (
                            <div className="space-y-2">
                              <Textarea
                                placeholder="Ablehnungsgrund eingeben..."
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                className="min-w-[200px]"
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleReject(request.id)}
                                  disabled={processingId === request.id}
                                >
                                  Ablehnen
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setRejectingId(null);
                                    setRejectionReason('');
                                  }}
                                >
                                  Abbrechen
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleApprove(request.id)}
                                disabled={processingId === request.id}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Genehmigen
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => setRejectingId(request.id)}
                                disabled={processingId === request.id}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Ablehnen
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Processed Requests */}
            {processedRequests.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Bearbeitete Anfragen ({processedRequests.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {processedRequests.map((request) => (
                    <div key={request.id} className={`border rounded-lg p-4 ${request.status === 'approved' ? 'bg-green-50/50' : 'bg-red-50/50'}`}>
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-5 w-5" />
                            <span className="font-semibold">{request.location_name}</span>
                            {getStatusBadge(request.status)}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <span>{request.customer_name}</span>
                            <span>{formatDate(request.created_at)}</span>
                          </div>
                          {request.rejection_reason && (
                            <p className="text-sm text-red-700">
                              <strong>Ablehnungsgrund:</strong> {request.rejection_reason}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};
